import { DRYDOCK_PROJECT_KINDS, type DrydockProjectKind } from '@felix/domain';
import { Alert, Button, Drawer, Group, Modal, Select, Textarea, TextInput } from '@mantine/core';
import {
  IconAlertTriangle,
  IconAnchor,
  IconBan,
  IconCheck,
  IconEdit,
  IconHistory,
  IconListCheck,
  IconPlus,
  IconShip,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import { usePmsQuery } from '../pms/PmsWorkParts.js';
import lib from '../pms-library/PmsLibrary.module.css';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { CloseoutModal, DockModal } from './CloseoutWizard.js';
import classes from './Drydock.module.css';
import { DrydockAuditDrawer } from './DrydockAuditTrail.js';
import { useDrydockErrorText } from './errors.js';
import { DrydockReportButtons } from './ReportButtons.js';
import { type ReportMode, useDrydockReportExport } from './report-export.js';
import type { DrydockProjectSource, DrydockScopeSource } from './source.js';
import type {
  DrydockProject,
  DrydockProjectInput,
  DrydockProjects,
  PdiInspection,
} from './types.js';

export interface DrydockProjectsScreenProps {
  source: DrydockProjectSource;
  /** Bulgu ve işler (Faz 5 raporları: Yard Job List ve kapanış raporu); verilmezse düğmeler yok. */
  scope?: DrydockScopeSource;
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; data: DrydockProjects };

const STATE_TONE: Record<DrydockProject['state'], StatusTone> = {
  planning: 'info',
  in_yard: 'warning',
  completed: 'success',
  cancelled: 'neutral',
};

const ALL = 'all';

/**
 * Maintenance (PMS) ▸ Dry Docking ▸ Projects (ADR 0129 §5, §9): geminin havuzlama projeleri ve
 * Pre-Docking denetim örnekleri. Gemide proje açılır, düzeltilir, gerekçeyle iptal edilir ve
 * denetim örneği son yayımlı kütüphane sürümüyle açılır; ofiste salt okunur, gemi süzgeciyle
 * (`?vessel=`). Yayımlı sürüm yoksa uyarı görünür ve denetim açılamaz (sahip kararı 2026-10-26).
 * Faz 5 (ADR 0129 Ek Faz 5): `?project=` çekmeceyi açar; çekmecede denetim izi, Yard Job List ve
 * kapanış raporu.
 */
export function DrydockProjectsScreen({ source, scope }: DrydockProjectsScreenProps) {
  const { t } = useTranslation();
  const [query, setQuery] = usePmsQuery();
  const ship = source.scope === 'ship';
  const vesselId = ship ? null : query.get('vessel');
  const [state, setState] = useState<State>({ kind: 'loading' });
  // Derin bağlantı (`?project=`): filo görünümünden ve bildirimlerden açılır
  const openId = query.get('project');
  const setOpenId = useCallback((id: string | null) => setQuery({ project: id }), [setQuery]);
  const [editing, setEditing] = useState<{ project: DrydockProject | null } | null>(null);

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', data: await source.load(vesselId) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);

  const data = state.kind === 'ready' ? state.data : null;
  const canCreate = ship && (data?.can.create ?? false) && Boolean(source.save);

  const header = (
    <PageHeader
      title={t('drydock.projects.title')}
      description={t(ship ? 'drydock.projects.description' : 'drydock.projects.officeDescription')}
      actions={
        canCreate ? (
          <Button
            leftSection={<IconPlus size={16} aria-hidden />}
            onClick={() => setEditing({ project: null })}
          >
            {t('drydock.projects.new')}
          </Button>
        ) : undefined
      }
    />
  );

  if (state.kind === 'loading') {
    return (
      <div className={lib.screen}>
        {header}
        <TableSkeleton columns={7} label={t('drydock.projects.title')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={lib.screen}>
        {header}
        <Panel>
          <LoadError
            code={state.code}
            title={t('drydock.projects.error')}
            onRetry={() => {
              setState({ kind: 'loading' });
              void load();
            }}
          />
        </Panel>
      </div>
    );
  }

  const projects = state.data.projects;
  const opened = projects.find((p) => p.id === openId) ?? null;
  const vessels = state.data.vessels ?? [];
  const unpublished = state.data.libraryPublished === false;

  return (
    <div className={lib.screen}>
      {header}
      {ship ? null : (
        <div className={classes.filters}>
          <Select
            label={t('drydock.projects.vessel')}
            data={[
              { value: ALL, label: t('drydock.projects.allVessels') },
              ...vessels.map((v) => ({ value: v.id, label: v.name })),
            ]}
            value={vesselId ?? ALL}
            allowDeselect={false}
            onChange={(value) => setQuery({ vessel: value === ALL ? null : value })}
          />
        </div>
      )}
      {unpublished ? (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} aria-hidden />}>
          {t('drydock.projects.noLibrary')}
        </Alert>
      ) : null}
      <Panel flush>
        <ProjectTable projects={projects} ship={ship} onOpen={setOpenId} />
      </Panel>
      <ProjectDrawer
        project={opened}
        source={source}
        scope={scope}
        can={ship ? state.data.can : { create: false, edit: false, void: false }}
        libraryPublished={!unpublished}
        onClose={() => setOpenId(null)}
        onEdit={(project) => setEditing({ project })}
        onChanged={load}
      />
      <ProjectFormDrawer
        target={editing}
        source={source}
        onClose={() => setEditing(null)}
        onSaved={async (saved) => {
          setEditing(null);
          await load();
          setOpenId(saved.id);
        }}
      />
    </div>
  );
}

/** Proje tablosu: gemide geminin, ofiste filonun (Vessel sütunuyla) projeleri. */
export function ProjectTable({
  projects,
  ship,
  onOpen,
}: {
  projects: readonly DrydockProject[];
  ship: boolean;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drydock.projectColumns.${key}`);
  return (
    <>
      {projects.length === 0 ? (
        <EmptyState title={t('drydock.projects.empty')} />
      ) : (
        <section
          className={table.tableWrap}
          aria-label={t('drydock.projects.list')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
          tabIndex={0}
        >
          <table className={`${table.table} ${classes.checkpointTable}`}>
            <thead>
              <tr>
                <th scope="col">{k('no')}</th>
                {ship ? null : <th scope="col">{k('vessel')}</th>}
                <th scope="col">{k('title')}</th>
                <th scope="col">{k('kind')}</th>
                <th scope="col">{k('yard')}</th>
                <th scope="col">{k('planned')}</th>
                <th scope="col">{k('state')}</th>
                <th scope="col">{k('inspection')}</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => {
                const last = p.inspections.at(-1);
                return (
                  <tr key={p.id}>
                    <td className={classes.mono}>
                      <button
                        type="button"
                        className={classes.linkButton}
                        onClick={() => onOpen(p.id)}
                      >
                        {p.projectNo}
                      </button>
                    </td>
                    {ship ? null : <td>{p.vesselName ?? '—'}</td>}
                    <td>{p.title}</td>
                    <td>{t(`drydock.kinds.${p.kind}`)}</td>
                    <td>{p.yardName ?? '—'}</td>
                    <td className={classes.mono}>
                      {p.plannedStart || p.plannedEnd
                        ? `${formatDay(p.plannedStart)} – ${formatDay(p.plannedEnd)}`
                        : '—'}
                    </td>
                    <td>
                      <StatusBadge tone={STATE_TONE[p.state]}>
                        {t(`drydock.states.${p.state}`)}
                      </StatusBadge>
                    </td>
                    <td className={classes.mono}>
                      {last
                        ? `${last.inspectionNo} · ${t(`drydock.inspectionStates.${last.state}`)}`
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

function ProjectDrawer({
  project,
  source,
  scope,
  can,
  libraryPublished,
  onClose,
  onEdit,
  onChanged,
}: {
  project: DrydockProject | null;
  source: DrydockProjectSource;
  scope?: DrydockScopeSource | undefined;
  can: DrydockProjects['can'];
  libraryPublished: boolean;
  onClose: () => void;
  onEdit: (project: DrydockProject) => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const shell = useShell();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [yard, setYard] = useState<null | 'dock_in' | 'undock' | 'close'>(null);
  const [inspection, setInspection] = useState<PdiInspection | null>(null);
  const [auditing, setAuditing] = useState(false);
  const reports = useDrydockReportExport(source);
  // Başka proje açılınca ileti ve iptal penceresi sıfırlanır (önceki değer kalıbı)
  const [seen, setSeen] = useState(project?.id);
  if (seen !== project?.id) {
    setSeen(project?.id);
    setError(null);
    setCancelling(false);
    setYard(null);
    setAuditing(false);
  }

  // Kapalı proje salt okunur (Faz 4, ADR 0129 #1956)
  const live = project !== null && project.state !== 'cancelled' && project.state !== 'completed';
  const hasOpen = project?.inspections.some((i) => i.state === 'open') ?? false;
  const approve = can.approve === true;
  const inYard = project?.state === 'in_yard';
  // After Docking: tersanede, açık denetim yok ve kapalı bir denetim var (R5)
  const afterDockingReady =
    inYard && !hasOpen && (project?.inspections.some((i) => i.state === 'closed') ?? false);

  async function openAfterDocking() {
    if (!project || !source.afterDocking) return;
    setBusy(true);
    setError(null);
    try {
      const opened = await source.afterDocking(project.id);
      await onChanged();
      if (shell) shell.navigate(inspectionPath(opened.id));
      else setInspection(opened);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function openInspection() {
    if (!project || !source.openInspection) return;
    setBusy(true);
    setError(null);
    try {
      const opened = await source.openInspection(project.id);
      await onChanged();
      // Kabukta denetim ekranına geçilir; kabuk yoksa (test) özet penceresi
      if (shell) shell.navigate(inspectionPath(opened.id));
      else setInspection(opened);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  /** Yard Job List PDF'i: projenin işleri (onaysız iş işaretli). */
  async function jobListReport(mode: ReportMode) {
    if (!project || !scope) return null;
    try {
      return await reports.jobList(await scope.load(project.id), mode);
    } catch (err) {
      return errorText(err);
    }
  }

  /** Kapanış raporu: kapanmamış projede önizleme planıyla ("PREVIEW – PROJECT NOT CLOSED"). */
  async function closeoutReport(mode: ReportMode) {
    if (!project || !scope) return null;
    try {
      const data = await scope.load(project.id);
      const preview =
        live && source.closeout ? await source.closeout(project.id).catch(() => null) : null;
      return await reports.closeout(project, { scope: data, closeout: preview }, mode);
    } catch (err) {
      return errorText(err);
    }
  }

  async function show(id: string) {
    setError(null);
    if (shell) {
      shell.navigate(inspectionPath(id));
      return;
    }
    try {
      setInspection(await source.inspection(id));
    } catch (err) {
      setError(errorText(err));
    }
  }

  const fields: [string, string][] = project
    ? [
        ['kind', t(`drydock.kinds.${project.kind}`)],
        ['yardName', project.yardName ?? '—'],
        ['yardLocation', project.yardLocation ?? '—'],
        ['plannedStart', formatDay(project.plannedStart)],
        ['plannedEnd', formatDay(project.plannedEnd)],
        ['notes', project.notes ?? '—'],
      ]
    : [];
  // Faz 4 alanları (eski sunucuda yok)
  const yardFields: [string, string][] = project
    ? [
        ['dockedOn', project.dockedOn ? formatDay(project.dockedOn) : '—'],
        ['undockedOn', project.undockedOn ? formatDay(project.undockedOn) : '—'],
        ...(project.closedOn
          ? ([
              ['closedOn', formatDay(project.closedOn)],
              ['closeNote', project.closeNote ?? '—'],
            ] as [string, string][])
          : []),
      ]
    : [];

  return (
    <>
      <Drawer
        opened={project !== null}
        onClose={onClose}
        closeButtonProps={{ 'aria-label': t('drydock.close') }}
        position="right"
        size="lg"
        title={project ? `${project.projectNo} · ${project.title}` : ''}
      >
        {project ? (
          <div className={classes.detail}>
            <StatusBadge tone={STATE_TONE[project.state]}>
              {t(`drydock.states.${project.state}`)}
            </StatusBadge>
            {project.cancelReason ? <Alert color="gray">{project.cancelReason}</Alert> : null}
            {error ? <Alert color="red">{error}</Alert> : null}
            <dl className={classes.fields}>
              {fields.map(([key, value]) => (
                <div key={key}>
                  <dt>{t(`drydock.projectFields.${key}`)}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
              {project.dockedOn !== undefined
                ? yardFields.map(([key, value]) => (
                    <div key={key}>
                      <dt>{t(`drydock.yard.${key}`)}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))
                : null}
            </dl>
            {project.state === 'completed' ? (
              <Alert color="gray">{t('drydock.yard.closed')}</Alert>
            ) : null}
            <Panel title={t('drydock.projects.inspections')}>
              {project.inspections.length === 0 ? (
                <p className={classes.muted}>{t('drydock.projects.noInspection')}</p>
              ) : (
                <ul className={classes.inspections}>
                  {project.inspections.map((i) => (
                    <li key={i.id}>
                      <button
                        type="button"
                        className={classes.linkButton}
                        title={t('drydock.projects.goInspection')}
                        onClick={() => void show(i.id)}
                      >
                        {i.inspectionNo}
                      </button>
                      {i.kind ? (
                        <span className={classes.muted}>{t(`drydock.yard.kinds.${i.kind}`)}</span>
                      ) : null}
                      <span className={classes.muted}>
                        {t('drydock.projects.libraryVersion', {
                          version: i.libraryVersion ?? '—',
                          count: i.checkpoints ?? 0,
                        })}
                      </span>
                      <StatusBadge tone={i.state === 'open' ? 'info' : 'neutral'}>
                        {t(`drydock.inspectionStates.${i.state}`)}
                      </StatusBadge>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
            <Group gap="xs" wrap="wrap">
              {source.audit ? (
                <Button
                  variant="default"
                  leftSection={<IconHistory size={16} aria-hidden />}
                  onClick={() => setAuditing(true)}
                >
                  {t('drydockAudit.open')}
                </Button>
              ) : null}
              {scope ? (
                <>
                  <DrydockReportButtons
                    label={t('drydockReport.actions.jobList')}
                    busy={reports.busy}
                    run={jobListReport}
                  />
                  <DrydockReportButtons
                    label={t('drydockReport.actions.closeout')}
                    busy={reports.busy}
                    run={closeoutReport}
                  />
                </>
              ) : null}
            </Group>
            {live ? (
              <Group justify="flex-end" gap="xs">
                {can.void && source.cancel ? (
                  <Button
                    variant="default"
                    color="red"
                    leftSection={<IconBan size={16} aria-hidden />}
                    onClick={() => setCancelling(true)}
                  >
                    {t('drydock.projects.cancelProject')}
                  </Button>
                ) : null}
                {can.edit && source.save ? (
                  <Button
                    variant="default"
                    leftSection={<IconEdit size={16} aria-hidden />}
                    onClick={() => onEdit(project)}
                  >
                    {t('drydock.actions.edit')}
                  </Button>
                ) : null}
                {approve && source.dock && project.state === 'planning' ? (
                  <Button
                    variant="default"
                    leftSection={<IconAnchor size={16} aria-hidden />}
                    onClick={() => setYard('dock_in')}
                  >
                    {t('drydock.yard.dockIn')}
                  </Button>
                ) : null}
                {approve && source.dock && inYard && !project.undockedOn ? (
                  <Button
                    variant="default"
                    leftSection={<IconShip size={16} aria-hidden />}
                    onClick={() => setYard('undock')}
                  >
                    {t('drydock.yard.undock')}
                  </Button>
                ) : null}
                {can.create && source.afterDocking && afterDockingReady ? (
                  <Button
                    variant="default"
                    leftSection={<IconListCheck size={16} aria-hidden />}
                    loading={busy}
                    title={t('drydock.yard.afterDockingHint')}
                    onClick={() => void openAfterDocking()}
                  >
                    {t('drydock.yard.afterDocking')}
                  </Button>
                ) : null}
                {approve && source.close && inYard ? (
                  <Button
                    leftSection={<IconCheck size={16} aria-hidden />}
                    onClick={() => setYard('close')}
                  >
                    {t('drydock.yard.closeProject')}
                  </Button>
                ) : null}
                {can.create && source.openInspection && !hasOpen ? (
                  <Button
                    leftSection={<IconListCheck size={16} aria-hidden />}
                    loading={busy}
                    disabled={!libraryPublished}
                    title={libraryPublished ? undefined : t('drydock.projects.noLibrary')}
                    onClick={() => void openInspection()}
                  >
                    {t('drydock.projects.openInspection')}
                  </Button>
                ) : null}
              </Group>
            ) : null}
          </div>
        ) : null}
      </Drawer>
      <CancelModal
        project={cancelling ? project : null}
        source={source}
        onClose={() => setCancelling(false)}
        onDone={async () => {
          setCancelling(false);
          await onChanged();
        }}
      />
      {project && (yard === 'dock_in' || yard === 'undock') ? (
        <DockModal
          project={project}
          action={yard}
          source={source}
          onClose={() => setYard(null)}
          onDone={async () => {
            setYard(null);
            await onChanged();
          }}
        />
      ) : null}
      {project && yard === 'close' ? (
        <CloseoutModal
          project={project}
          source={source}
          report={scope ? { run: closeoutReport, busy: reports.busy } : undefined}
          onClose={() => setYard(null)}
          onDone={async () => {
            setYard(null);
            await onChanged();
          }}
        />
      ) : null}
      <InspectionModal inspection={inspection} onClose={() => setInspection(null)} />
      <DrydockAuditDrawer
        project={auditing ? project : null}
        source={source}
        onClose={() => setAuditing(false)}
      />
    </>
  );
}

/** Denetim ekranının adresi (Maintenance ▸ Dry Docking ▸ Pre-Docking Inspection). */
export function inspectionPath(id: string): string {
  return `/pms/drydock/inspection?inspection=${encodeURIComponent(id)}`;
}

function InspectionModal({
  inspection,
  onClose,
}: {
  inspection: PdiInspection | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      opened={inspection !== null}
      onClose={onClose}
      size="lg"
      title={inspection ? t('drydock.inspection.title', { no: inspection.inspectionNo }) : ''}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      {inspection ? (
        <div className={classes.detail}>
          <p>
            {t('drydock.inspection.frozen', {
              date: formatDay(inspection.openedOn),
              version: inspection.libraryVersion ?? '—',
            })}
          </p>
          {inspection.latestVersion !== null &&
          inspection.latestVersion !== inspection.libraryVersion ? (
            <Alert color="gray">
              {t('drydock.projects.libraryNow', { version: inspection.latestVersion })}
            </Alert>
          ) : null}
          <ul className={classes.areaList}>
            {inspection.tree.map((area) => (
              <li key={area.code}>
                <span>
                  <span className={classes.mono}>{area.code}</span> {area.name}
                </span>
                <span className={classes.muted}>
                  {area.systems.reduce((sum, s) => sum + s.checkpoints.length, 0)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Modal>
  );
}

function CancelModal({
  project,
  source,
  onClose,
  onDone,
}: {
  project: DrydockProject | null;
  source: DrydockProjectSource;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [seen, setSeen] = useState(project?.id);
  if (seen !== project?.id) {
    setSeen(project?.id);
    setReason('');
    setError(null);
  }

  async function confirm() {
    if (!project || !source.cancel) return;
    setSaving(true);
    setError(null);
    try {
      await source.cancel({ id: project.id, rowVersion: project.rowVersion, reason });
      await onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      opened={project !== null}
      onClose={onClose}
      title={project ? t('drydock.projects.cancelTitle', { no: project.projectNo }) : ''}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p>{t('drydock.projects.cancelBody')}</p>
        {error ? <Alert color="red">{error}</Alert> : null}
        <Textarea
          label={t('drydock.projects.reason')}
          required
          autosize
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button color="red" loading={saving} onClick={() => void confirm()}>
            {t('drydock.projects.cancelProject')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

function formOf(project: DrydockProject | null): DrydockProjectInput {
  return {
    title: project?.title ?? '',
    kind: project?.kind ?? 'dry_docking',
    yardName: project?.yardName ?? null,
    yardLocation: project?.yardLocation ?? null,
    plannedStart: project?.plannedStart ?? null,
    plannedEnd: project?.plannedEnd ?? null,
    notes: project?.notes ?? null,
  };
}

function ProjectFormDrawer({
  target,
  source,
  onClose,
  onSaved,
}: {
  target: { project: DrydockProject | null } | null;
  source: DrydockProjectSource;
  onClose: () => void;
  onSaved: (saved: DrydockProject) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const project = target?.project ?? null;
  const [form, setForm] = useState<DrydockProjectInput>(() => formOf(project));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(formOf(target?.project ?? null));
    setError(null);
  }, [target]);
  const set = <K extends keyof DrydockProjectInput>(key: K, value: DrydockProjectInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const text = (value: string) => (value.trim() === '' ? null : value);

  async function save() {
    if (!source.save) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await source.save(
        project
          ? { id: project.id, rowVersion: project.rowVersion, project: form }
          : { project: form },
      );
      await onSaved(saved);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      opened={target !== null}
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
      position="right"
      size="lg"
      title={
        project
          ? t('drydock.projects.titleEdit', { no: project.projectNo })
          : t('drydock.projects.titleNew')
      }
    >
      <div className={classes.form}>
        {error ? <Alert color="red">{error}</Alert> : null}
        <TextInput
          label={t('drydock.projectFields.title')}
          required
          value={form.title}
          onChange={(e) => set('title', e.currentTarget.value)}
        />
        <Select
          label={t('drydock.projectFields.kind')}
          data={DRYDOCK_PROJECT_KINDS.map((value) => ({
            value,
            label: t(`drydock.kinds.${value}`),
          }))}
          value={form.kind}
          allowDeselect={false}
          onChange={(value) => set('kind', (value as DrydockProjectKind | null) ?? 'dry_docking')}
        />
        <TextInput
          label={t('drydock.projectFields.yardName')}
          value={form.yardName ?? ''}
          onChange={(e) => set('yardName', text(e.currentTarget.value))}
        />
        <TextInput
          label={t('drydock.projectFields.yardLocation')}
          value={form.yardLocation ?? ''}
          onChange={(e) => set('yardLocation', text(e.currentTarget.value))}
        />
        <DayInput
          label={t('drydock.projectFields.plannedStart')}
          clearable
          value={form.plannedStart}
          onChange={(value) => set('plannedStart', value)}
        />
        <DayInput
          label={t('drydock.projectFields.plannedEnd')}
          clearable
          value={form.plannedEnd}
          minDate={form.plannedStart ?? undefined}
          onChange={(value) => set('plannedEnd', value)}
        />
        <Textarea
          label={t('drydock.projectFields.notes')}
          autosize
          minRows={2}
          value={form.notes ?? ''}
          onChange={(e) => set('notes', text(e.currentTarget.value))}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            {t('drydock.save')}
          </Button>
        </Group>
      </div>
    </Drawer>
  );
}
